import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  Box,
  Chip,
} from "@mui/material";
import { SortType } from "../../../../../../models/SortType";
import {
  OrderTypeEnum,
  ProjectManagementBasicDetails,
} from "../../../../../../models/ProjectManagement/ProjectManagementBasicDetails";
import { Link, useNavigate } from "react-router-dom";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import { StyledTableRow } from "../../../../../../styles/Shipmentstyles";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import ProgressBar from "../../../../../../views/components/common/progressBars/LinearProgressBar";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";

interface ProjectManagementTableProps {
  data: ProjectManagementBasicDetails[];
  maxHeight: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string) => void;
  minHeight?: number;
}

const ProjectManagementTable: React.FC<ProjectManagementTableProps> = ({
  data,
  maxHeight,
  page_no,
  limit,
  total_count,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortType,
  onSortChange,
  minHeight = window.innerHeight - 350,
}) => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const { userStore } = useStores();
  const canViewDispatchDate = userStore.hasPermission(
    EmployeeRoles.VIEW_DISPATCH_DATE,
  );

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                minWidth="80px"
                sortColumn={"order_id"}
                sortBy={sortBy}
                sortDirection={sortType}
                onSortChange={onSortChange}
                title="PROJECT ID"
                fontSize="0.875rem"
                fontWeight={700}
              />
              <AppTableHeaderCell
                title="STATUS"
                fontSize="0.875rem"
                fontWeight={700}
                minWidth="150px"
              />
              <AppTableHeaderCell
                title="LABEL"
                fontSize="0.875rem"
                fontWeight={700}
                minWidth="200px"
              />
              <AppTableHeaderCell
                title="CUSTOMER"
                fontSize="0.875rem"
                fontWeight={700}
                minWidth="300px"
              />
              <AppTableHeaderCell
                title="ORDER"
                fontSize="0.875rem"
                fontWeight={700}
                minWidth="100px"
              />
              <AppTableHeaderCell
                minWidth="80px"
                sortColumn={"id"}
                title="WAREHOUSE"
                fontSize="0.875rem"
                fontWeight={700}
              />
              <AppTableHeaderCell
                title="START DATE"
                sortColumn={"created_date"}
                sortBy={sortBy}
                sortDirection={sortType}
                onSortChange={onSortChange}
                fontSize="0.875rem"
                fontWeight={700}
              />
              {canViewDispatchDate && (
                <AppTableHeaderCell
                  title="DISPATCH DATE"
                  fontSize="0.875rem"
                  fontWeight={700}
                  sortColumn={"dispatch_date"}
                  sortBy={sortBy}
                  sortDirection={sortType}
                  onSortChange={onSortChange}
                />
              )}
              <AppTableHeaderCell
                title="PROJECT POC"
                fontSize="0.875rem"
                fontWeight={700}
              />
              <AppTableHeaderCell
                title="SALES POC"
                fontSize="0.875rem"
                fontWeight={700}
              />

              <AppTableHeaderCell
                title="ARCHIVED BY"
                fontSize="0.875rem"
                fontWeight={700}
              />

              <AppTableHeaderCell
                title="ARCHIVED DATE"
                fontSize="0.875rem"
                fontWeight={700}
              />
              <AppTableHeaderCell
                title="PROGRESS"
                fontSize="0.875rem"
                fontWeight={700}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item: ProjectManagementBasicDetails) => (
              <StyledTableRow key={item.id}>
                <AppTableRowCell
                  minWidth="80px"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  <Link
                    to={
                      item.order_type_id === OrderTypeEnum.POD
                        ? `/v1/project/${item.id}`
                        : `/projects/${item.id}`
                    }
                    target="_blank"
                    style={{
                      textDecoration: "underline",
                      color: "black",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.id}
                  </Link>
                </AppTableRowCell>

                <AppTableRowCell>{item.label || "-"}</AppTableRowCell>

                <AppTableRowCell minWidth="80px">
                  <Chip
                    label={item.archived ? "Archived" : "Active"}
                    size="small"
                    color={item.archived ? "error" : "success"}
                    variant="outlined"
                  />
                </AppTableRowCell>

                <AppTableRowCell>{item?.customer_name || "-"}</AppTableRowCell>

                <AppTableRowCell
                  minWidth="5rem"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  <Link
                    to={`/orders/add-orders?orderId=${item.id}&backToProject=true`}
                    target="_blank"
                    style={{
                      textDecoration: "underline",
                      color: "black",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.id}
                  </Link>
                </AppTableRowCell>
                <AppTableRowCell minWidth="10rem">
                  {item.warehouse_identifier || "-"}
                </AppTableRowCell>
                <AppTableRowCell minWidth="5rem">
                  {formatDate(item.created_date) || "-"}
                </AppTableRowCell>

                {canViewDispatchDate && (
                  <AppTableRowCell minWidth="5rem">
                    {formatDate(item.dispatch_date) || "-"}
                  </AppTableRowCell>
                )}

                <AppTableRowCell minWidth="5rem">
                  {item.pm_admin_name || "-"}
                </AppTableRowCell>

                <AppTableRowCell minWidth="6rem">
                  {item.sales_admin_name || "-"}
                </AppTableRowCell>

                <AppTableRowCell minWidth="5rem">
                  {item.archived ? item.archived_adminname : "-"}
                </AppTableRowCell>

                <AppTableRowCell minWidth="5rem">
                  {formatDate(item.archived_date) || "-"}
                </AppTableRowCell>

                <AppTableRowCell
                  minWidth="9.375rem"
                  sx={{
                    display: "table-cell",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <ProgressBar
                      progressPercentage={item.progress || 0}
                      color={
                        !item.progress || item.progress <= 33
                          ? "rgba(215, 69, 143, 1)"
                          : item.progress > 33 && item.progress <= 66
                            ? "rgba(117, 103, 248, 1)"
                            : item.progress > 66
                              ? "rgba(82, 230, 202, 1)"
                              : ""
                      }
                      height={8}
                      width={92}
                    />
                    <CustomTypography variant="body3" fontWeight={500}>
                      {item.progress !== undefined
                        ? item.progress < 10
                          ? `0${item.progress}%`
                          : `${item.progress}%`
                        : "0%"}
                    </CustomTypography>
                  </Box>
                </AppTableRowCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>

        {data.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 15,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="body2"
              fontWeight={700}
            >
              NO PROJECTS FOUND!
            </CustomTypography>
          </Box>
        )}
      </TableContainer>
      <AppTablePagenation
        fontSize="0.8rem"
        pageNo={page_no || 1}
        limit={limit || 0}
        totalCount={total_count || 0}
        onPageChange={onPageChange}
        handleRowsChange={handleRowsChange}
      />
    </>
  );
};

export default ProjectManagementTable;
